import { loadGoogleMapsAPI } from './googleMapsLoader';

interface LocalityInfoEntry {
  name?: string;
  description?: string;
  order?: number;
}

interface BigDataCloudResponse {
  locality?: string;
  city?: string;
  principalSubdivision?: string;
  postcode?: string;
  localityInfo?: {
    informative?: LocalityInfoEntry[];
    administrative?: LocalityInfoEntry[];
  };
}

export interface AddressDetails {
  address: string;
  suburb: string;
  council: string;
  postcode: string;
}

const findFirstMatching = (entries: LocalityInfoEntry[] | undefined, keywords: string[]): string => {
  if (!Array.isArray(entries)) {
    return '';
  }

  const lowerKeywords = keywords.map((keyword) => keyword.toLowerCase());

  for (const entry of entries) {
    const description = entry.description?.toLowerCase() || '';
    if (lowerKeywords.some((keyword) => description.includes(keyword))) {
      return entry.name || '';
    }
  }

  return '';
};

/**
 * Extract a detailed address representation from BigDataCloud reverse geocode response.
 */
export const extractAddressDetailsFromReverseGeocode = (data: BigDataCloudResponse | undefined | null): AddressDetails => {
  if (!data) {
    return {
      address: '',
      suburb: '',
      council: '',
      postcode: ''
    };
  }

  const informative = data.localityInfo?.informative;
  const administrative = data.localityInfo?.administrative;

  const houseNumber = findFirstMatching(informative, [
    'house number',
    'address range',
    'building number',
    'entrance'
  ]);

  const streetName = findFirstMatching(informative, [
    'street',
    'road',
    'route',
    'residential',
    'lane',
    'drive',
    'avenue',
    'boulevard',
    'way',
    'place',
    'circuit',
    'close'
  ]);

  const suburb = (
    data.locality ||
    findFirstMatching(informative, ['suburb', 'city district', 'neighbourhood', 'neighborhood', 'locality']) ||
    data.city ||
    findFirstMatching(administrative, ['suburb', 'city', 'district']) ||
    ''
  );

  const council = (
    findFirstMatching(administrative, ['local government area', 'city council', 'county', 'municipality']) ||
    data.principalSubdivision ||
    ''
  );

  const postcode = data.postcode || '';

  const address = (
    [houseNumber, streetName].filter(Boolean).join(' ').trim() ||
    streetName ||
    data.locality ||
    data.city ||
    ''
  );

  return {
    address,
    suburb,
    council,
    postcode
  };
};

interface NominatimResponse {
  display_name?: string;
  address?: Record<string, string | undefined>;
}

const firstTruthy = (values: Array<string | undefined>): string => {
  for (const value of values) {
    if (value && value.trim()) {
      return value.trim();
    }
  }
  return '';
};

const extractAddressDetailsFromNominatim = (data: NominatimResponse | undefined | null): AddressDetails => {
  if (!data) {
    return {
      address: '',
      suburb: '',
      council: '',
      postcode: ''
    };
  }

  const address = data.address || {};

  let houseNumber = firstTruthy([
    address.house_number,
    address.house,
    address.house_name,
    address.name,
    address.building,
    address.residential
  ]);

  let road = firstTruthy([
    address.road,
    address.street,
    address.residential,
    address.pedestrian,
    address.path,
    address.service,
    address.cycleway
  ]);

  // Attempt to parse display_name when explicit tokens are missing
  if ((!houseNumber || !road) && data.display_name) {
    const primarySegment = data.display_name.split(',')[0]?.trim() || '';
    const match = primarySegment.match(/^(\d+[\w\/-]*)\s+(.*)$/);

    if (match) {
      if (!houseNumber) {
        houseNumber = match[1];
      }

      if (!road) {
        road = match[2];
      }
    }
  }

  const fullAddress = [houseNumber, road].filter(Boolean).join(' ').trim()
    || data.display_name
    || '';

  const suburb = firstTruthy([
    address.suburb,
    address.city_district,
    address.neighbourhood,
    address.locality,
    address.town,
    address.village,
    address.hamlet,
    address.city,
    address.state_district
  ]);

  const council = firstTruthy([
    address.council,
    address.municipality,
    address.city_council,
    address.local_government_area,
    address.county,
    address.region,
    address.state_district,
    address.state
  ]);

  const postcode = address.postcode?.trim() || '';

  return {
    address: fullAddress,
    suburb,
    council,
    postcode
  };
};

const extractAddressFromGoogleComponents = (components: Array<{ long_name: string; short_name: string; types: string[] }>): AddressDetails => {
  const componentMap = new Map<string, { long_name: string; short_name: string; types: string[] }>();

  components.forEach((component) => {
    component.types.forEach((type) => {
      if (!componentMap.has(type)) {
        componentMap.set(type, component);
      }
    });
  });

  const getLongName = (type: string): string => componentMap.get(type)?.long_name || '';

  const streetNumber = firstTruthy([
    getLongName('street_number'),
    getLongName('premise'),
    getLongName('subpremise')
  ]);

  const route = firstTruthy([
    getLongName('route'),
    getLongName('intersection'),
    getLongName('point_of_interest')
  ]);

  const suburb = firstTruthy([
    getLongName('sublocality'),
    getLongName('sublocality_level_1'),
    getLongName('neighborhood'),
    getLongName('locality'),
    getLongName('postal_town')
  ]);

  const council = firstTruthy([
    getLongName('administrative_area_level_2'),
    getLongName('administrative_area_level_3'),
    getLongName('administrative_area_level_1'),
    getLongName('administrative_area_level_4'),
    getLongName('administrative_area_level_5'),
    getLongName('country')
  ]);

  const postcode = getLongName('postal_code');

  const address = [streetNumber, route].filter(Boolean).join(' ').trim();

  return {
    address,
    suburb,
    council,
    postcode
  };
};

const geocodeWithGoogleMaps = async (latitude: number, longitude: number): Promise<AddressDetails | null> => {
  try {
    await loadGoogleMapsAPI();
  } catch (error) {
    console.warn('Failed to load Google Maps API for reverse geocoding:', error);
    return null;
  }

  const googleMaps = (window as typeof window & { google?: any }).google;

  if (!googleMaps?.maps?.Geocoder) {
    return null;
  }

  const geocoder = new googleMaps.maps.Geocoder();

  try {
    const results = await new Promise<any[]>((resolve, reject) => {
      geocoder.geocode(
        { location: { lat: latitude, lng: longitude } },
        (responses: any[] | null, status: string) => {
          if (status === googleMaps.maps.GeocoderStatus.OK && responses && responses.length > 0) {
            resolve(responses);
          } else if (status === googleMaps.maps.GeocoderStatus.ZERO_RESULTS) {
            resolve([]);
          } else {
            reject(new Error(`Google geocoder failed: ${status}`));
          }
        }
      );
    });

    if (results.length === 0) {
      return null;
    }

    const primaryResult =
      results.find((result) => result.types?.includes('street_address')) ||
      results.find((result) => result.types?.includes('premise')) ||
      results.find((result) => result.types?.includes('route')) ||
      results[0];

    if (!primaryResult?.address_components) {
      return null;
    }

    const details = extractAddressFromGoogleComponents(primaryResult.address_components);

    if (primaryResult.formatted_address && !details.address) {
      const match = primaryResult.formatted_address.match(/^(\d+[\w\/-]*)\s+(.*?)(,|$)/);
      if (match) {
        details.address = `${match[1]} ${match[2]}`.trim();
      }
    }

    return details;
  } catch (error) {
    console.warn('Google reverse geocoding failed:', error);
    return null;
  }
};

/**
 * Attempt to fetch detailed address information from multiple providers, preferring
 * services that include street-level accuracy.
 */
export const fetchAddressDetailsFromCoordinates = async (latitude: number, longitude: number): Promise<AddressDetails> => {
  // Attempt 0: Google Maps Geocoder (preferred when available)
  const googleDetails = await geocodeWithGoogleMaps(latitude, longitude);
  if (googleDetails && (googleDetails.address || googleDetails.suburb)) {
    return googleDetails;
  }

  // Attempt 1: OpenStreetMap (Nominatim)
  try {
    const nominatimResponse = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}&addressdetails=1&zoom=20&email=support%40curbside.quest`
    );

    if (nominatimResponse.ok) {
      const nominatimData = await nominatimResponse.json();
      const details = extractAddressDetailsFromNominatim(nominatimData);

      if (details.address || details.suburb) {
        return details;
      }
    }
  } catch (error) {
    console.warn('Failed to fetch detailed address from Nominatim:', error);
  }

  // Attempt 2: BigDataCloud fallback
  try {
    const response = await fetch(
      `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`
    );

    if (response.ok) {
      const data = await response.json();

      // Try to enrich the street with a possible house number from locality strings
      const details = extractAddressDetailsFromReverseGeocode(data);

      if (!/\d/.test(details.address || '')) {
        const localityCandidates = [data.locality, data.city].filter(Boolean) as string[];

        for (const candidate of localityCandidates) {
          const match = candidate.match(/^(\d+[\w\/-]*)\s+(.*)$/);
          if (match) {
            details.address = match[0];
            if (!details.suburb) {
              details.suburb = match[2];
            }
            break;
          }
        }
      }

      return details;
    }
  } catch (error) {
    console.warn('Failed to fetch address from BigDataCloud:', error);
  }

  return {
    address: '',
    suburb: '',
    council: '',
    postcode: ''
  };
};
